import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';

import {useScrollAnchorWrapper} from './useScrollAnchorWrapper';
import {transient} from 'Application/configs/emotionTransientProps';

import type {WithChildren} from 'types/global';

/**
 * The `ComponentProps` interface defines the properties for the `ScrollAnchorWrapper` component.
 * This interface specifies the expected structure of props used by `ScrollAnchorWrapper` to create scrollable anchor areas on the page.
 * It includes properties for scroll targeting, accessibility support, and layout positioning to ensure proper navigation and user experience.
 */
interface ComponentProps {
    /**
     * The ID of the element that labels this section, used for accessibility purposes.
     * This property establishes an accessible relationship between the wrapper and its heading or label element.
     * Screen readers and assistive technologies use this reference to provide context about the section's content.
     */
    labelledBy: string;
    /**
     * The anchor ID string used to identify the scroll target for navigation links.
     * When this value matches a link's `blockId`, the page will scroll smoothly to this component's position.
     * The ID should be formatted as a hash anchor (e.g., `#section-id`) and can be null if no scroll behavior is needed.
     */
    linkId?: `#${string}` | null;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable automated tests.
     * The value is applied as a `data-cy` attribute to facilitate component selection in test frameworks like Cypress.
     *
     * @default 'ScrollAnchorWrapper'
     */
    testId?: string;
}

/**
 * A wrapper component that marks a scroll anchor target and exposes a ref for scrolling behavior.
 * This component is used to wrap content that should act as a scroll destination for anchor links. It internally
 * uses the `useScrollAnchorWrapper` hook to attach a `ref` to the wrapper element, allowing other components
 * (like navigation links) to scroll smoothly to this section. The `linkId` must correspond to the `blockId`
 * used in navigation links for anchor scrolling to work correctly.
 * An optional `testId` is provided to facilitate reliable selection during testing. This value is applied as a
 * `data-cy` attribute.
 *
 * @param props            The component props.
 * @param props.children   The content to be rendered inside the scroll anchor wrapper.
 * @param props.labelledBy The ID of the element that labels this section, used for accessibility.
 * @param props.linkId     The anchor ID used to identify the scroll target.
 * @param props.testId     A custom test ID used for automation selectors. Defaults to `'ScrollAnchorWrapper'`.
 * @returns A JSX element that wraps its children in a `div` with a ref and test ID for scroll and test support.
 *
 * @example
 * ```tsx
 * <ScrollAnchorWrapper linkId="about-us">
 *   <section>About us content</section>
 * </ScrollAnchorWrapper>
 * ```
 */
const ScrollAnchorWrapper = observer(({
    children,
    labelledBy,
    testId = 'ScrollAnchorWrapper'
}: WithChildren<ComponentProps>) => {
    const {ref} = useScrollAnchorWrapper();

    return (
        <Wrapper
            ref={ref}
            aria-labelledby={labelledBy}
            data-cy={testId}
        >
            {children}
        </Wrapper>
    );
});

ScrollAnchorWrapper.displayName = 'ScrollAnchorWrapper';

export {ScrollAnchorWrapper};

const Wrapper = styled('section', {...transient})`
    position: relative;
`;